import { ApiClient } from '@auth0/auth0-api-js';
import { getCookie } from 'hono/cookie';
import { createMiddleware } from 'hono/factory';
import { z } from 'zod';
import { AccountId, AuthAccount } from './types/account.ts';

declare module 'hono' {
  interface ContextVariableMap {
    account: AuthAccount;
    roles: Roles;
  }
}

export const Claims = z.object({
  'shiver/account': z.object({ id: AccountId }),
  'shiver/roles': z.array(z.string()),
});
export type Claims = z.infer<typeof Claims>;

export type Roles = {
  cms?: { type: 'admin' };
  account?: { type: 'admin' };
  organization?: { type: 'admin'; ids: Array<string>; isDangerousSuperOrganizationAdmin?: boolean };
};

export function auth() {
  const apiClient = new ApiClient({
    domain: process.env.AUTH0_DOMAIN!,
    audience: process.env.AUTH0_AUDIENCE!,
  });

  return createMiddleware<{
    Variables: {
      account: AuthAccount;
      roles: Roles;
    };
  }>(async (c, next) => {
    let token: string | undefined;

    const authorization = c.req.header('authorization');

    // TODO: Cookieベースに統一する
    if (authorization) {
      const [type, token_] = authorization?.split(' ') ?? [];
      if (type !== 'Bearer') {
        return c.body(null, 401);
      }
      token = token_;
    } else if (c.req.header('cookie')) {
      const token_ = getCookie(c, 'token');
      if (!token_) {
        return c.body(null, 401);
      }
      token = token_;
    }

    if (!token) {
      return c.body(null, 401);
    }

    let decodedAndVerifiedToken;

    try {
      decodedAndVerifiedToken = await apiClient.verifyAccessToken({
        accessToken: token,
      });
    } catch {
      return c.body(null, 403);
    }

    const claims = Claims.parse(decodedAndVerifiedToken);
    const accountId = claims['shiver/account'].id;
    const roles = parseRoles(claims);

    if (import.meta.env.DEV) {
      // ローカルではauth0の個々人のアカウントは存在していないため回避
      c.set('account', AuthAccount.parse({ id: accountId, name: 'me' }));
    } else {
      const account = await c.get('prisma').account.account.findUnique({
        select: { email: true },
        where: { id: accountId },
      });

      if (!account) {
        return c.body(null, 403);
      }

      c.set('account', AuthAccount.parse({ id: accountId, name: account.email }));
    }

    c.set('roles', roles);

    return next();
  });
}

export function parseRoles(claims: Claims) {
  const roles: Roles = {};

  for (const role of claims['shiver/roles']) {
    if (role === 'cms.admin') {
      roles.cms = { type: 'admin' };
    } else if (role === 'account.admin') {
      roles.account = { type: 'admin' };
    } else if (role === 'account.dangerousSuperOrganizationAdmin') {
      roles.organization ??= { type: 'admin', ids: [] };
      roles.organization.isDangerousSuperOrganizationAdmin = true;
    } else if (role.startsWith('account.organizationAdmin:')) {
      const organizationId = role.split(':')[1];
      if (organizationId) {
        roles.organization ??= { type: 'admin', ids: [] };
        roles.organization.ids.push(organizationId);
      }
    }
  }

  return roles;
}
